import type {
  CharacterIndexInput,
  CharacterIndexSourceAnchor
} from "./types.js";
import {
  mapValidatedWindowRangeToSourceAnchors,
  prepareSingleWitnessCharacterExtractionCall,
  validatePreparedExtractionCall,
  type CharacterExtractionScope,
  type CharacterExtractionSourceMapSpan,
  type PreparedCharacterExtractionScopeMetadata,
  type PreparedCharacterExtractionValidationIssue
} from "./extractionPreparation.js";
import type { CharacterMentionTextRange } from "./mentionGrounding.js";
import type {
  SingleWitnessCoreferenceChainV2Figure,
  SingleWitnessCoreferenceChainV2Mention
} from "./coreferenceChainExtractionV2.js";

export const SINGLE_WITNESS_FOCALIZATION_CHARACTERIZATION_EXTRACTION_PROMPT_VERSION =
  "single-witness-focalization-characterization-extraction-v1";

const FOCALIZATION_CHARACTERIZATION_PROMPT_PREFIX = [
  "# Focalization & characterization extraction — classical Arabic witness (proposal v1)",
  "",
  "## Inputs",
  "1. **TEXT** — the witness, verbatim.",
  "2. **FIGURES** — the coreference output for this same text (the `figures` JSON from the coreference-chain prompt v2): figure `id`s, `kind`, anchors, chains with mention `id`s. **All attribution in this task uses these ids.** Do not re-identify figures; if a referring expression cannot be matched to a FIGURES id, skip the span and list it under `unmatched`.",
  "",
  "## Goal",
  "Extract two linked layers, each as verbatim spans tied to figure ids:",
  "",
  "- **Layer A — focalization markers**: every textual device that filters narrative information through a perceiving, knowing, feeling, or intending instance (whose inside the text opens, and whose knowledge it restricts).",
  "- **Layer B — characterization spans**: every span that ascribes a trait, state, appearance, evaluation, motive, habit, or reputation to a figure, identified by a **closed grammatical device inventory**, with its source voice.",
  "",
  "## Discipline (inherited, non-negotiable)",
  "- **Single, self-contained witness.** No knowledge of the wider work or tradition; no canonicalization.",
  "- **Verbatim always.** Every `span`, `marker`, `scope`, `context` is copied exactly.",
  "- **Closed inventories decide.** A span enters only by matching a device or marker class below — no impressionistic additions.",
  "- **No interpretation.** In direct ascription the trait word is the text's own — copy it. In indirect devices, extract the vehicle span only; **never output an inferred trait** (e.g., never add \"cowardly\" because a figure flees). [Toggle: `inferred_trait` field for downstream use — default OFF.]",
  "- **No reliability or truth judgments.** Whether a characterization is sincere, ironic, or false is downstream; the source attribution carries everything needed.",
  "- **No figure typology**, no narrative-unit selection. Ignore `[...]`.",
  "",
  "## Attribution frame — the narrator stack",
  "Reuse the speech-stack discipline of the coreference prompt:",
  "- **Narrator levels.** N0 = the witness's outermost voice (including conventional frames: زعموا، قال الراوي، حُكي). Each act of narration inside a quote opens a deeper level (N1, N2 …). A character telling a tale is the narrator of that tale's level.",
  "- Every extracted item carries a **source**:",
  "  - `{\"voice\":\"narrator\",\"level\":<n>,\"narrator\":\"<F-id or \\\"frame\\\">\"}` — the innermost narrating instance at that point;",
  "  - `{\"voice\":\"figure\",\"figure\":\"<F-id>\",\"channel\":\"speech\"|\"interior\"}` — uttered dialogue vs. interior discourse (قال في نفسه…);",
  "  - `{\"voice\":\"communal\"}` — story-world common knowledge (عُرف بـ، اشتهر بـ، كان يقال له على وجه الوصف).",
  "- **Derived views, not annotation choices.** Do not classify spans as \"authorial / speech / self / other / action.\" Those categories are computed afterward from the fields: narrator-sourced = authorial description; `channel:\"speech\"` = characterization in character speech; `source.figure == target` = self-perception; `source.figure != target` = others' perception; action-class devices (§B3) = characterization through action. The extractor records grammar and ids only.",
  "",
  "---",
  "",
  "## Layer A — focalization markers",
  "",
  "### A1–A7: marker classes (closed)",
  "Extract every finite occurrence whose **experiencer** is a FIGURES id (or a narrator level for A6). Record: `marker` (the verb/formula token, verbatim), `class`, `experiencer`, `scope` (verbatim minimal complement: the أنّ/أنْ-clause, the object, or the perceived-event clause), `context`.",
  "",
  "- **A1 perception**: رأى، نظر، أبصر، لمح، سمع، وجد (discovery), شمّ، ذاق، لمس، أحسّ.",
  "- **A2 cognition**: علم، عرف، فهم، فطن، ظنّ، حسب، خال، زعم، شكّ، أيقن، تبيّن، ذكر، نسي، درى.",
  "- **A3 affect**: خاف، فزع، فرح، حزن، غضب، أحبّ، كره، حسد، رجا، اشتهى، أعجبه، ساءه، سرّه، اشتدّ عليه.",
  "- **A4 volition / intention**: أراد، همّ بـ، عزم، نوى، طمع في، أمّل، أحبّ أنْ، كره أنْ.",
  "- **A5 interior discourse formulas**: قال في نفسه، حدّث نفسه، أضمر، أسرّ(ها) في نفسه، وقع في نفسه/قلبه/خَلَده، خطر بباله. The quote governed by an A5 formula is `channel:\"interior\"` for everything inside it.",
  "- **A6 narratorial evidential distance**: زعموا، يقال، قيل، ذكروا، حُكي، بلغني، والله أعلم — experiencer = the narrator level that utters them. These mark the voice's epistemic stance toward its own tale.",
  "- **A7 knowledge asymmetry (ignorance markers)**: لم يعلم، لا يدري، وهو لا يشعر، من حيث لا يعلم/لا يحتسب، وهو غافل. Extract with experiencer = the ignorant figure and scope = the withheld content if expressed. (These are the grammatical substrate of dramatic irony; extract, do not interpret.)",
  "",
  "### A-exclusions",
  "- Markers inside **gnomic material** (maxims, morals — the test from the coreference prompt: could it stand as a proverb outside this story?) bind to generic referents, not figures — exclude.",
  "- **Phatic / discourse uses**: أرأيتَ \"tell me\", ألا ترى أنّ as argumentative formula, اعلمْ أنّ as didactic opener — exclude as non-focalizing.",
  "- The **لمّا + perception** pivot (فلمّا رأى/سمع/علم …) is the canonical focalized-narration construction — extract normally under A1/A2; no special record.",
  "",
  "---",
  "",
  "## Layer B — characterization spans",
  "",
  "A characterization record: `targets` (array of FIGURES ids — the figure(s) characterized), `devices` (one or more tags below, primary first), `span` (verbatim), `context`, `source` (§ attribution), optional `mention_ref` (when the span is or contains a chain mention id from FIGURES).",
  "",
  "Targets must be figures (persons, agentive non-humans, groups). Traits of places and objects are out of scope.",
  "",
  "### B1. Direct ascription devices (the trait/state/appearance is stated)",
  "1. **naat** — نعت within an NP: الوزيرُ الحكيمُ، رجلٌ صالحٌ. When the NP is a chain mention, cite it via `mention_ref` instead of duplicating the form.",
  "2. **khabar** — predicate of a nominal sentence, incl. إنّ-intensified with لام: هو شجاعٌ؛ إنّك لجريءٌ.",
  "3. **kana_pred** — كان وأخواتها + predicate: كان حكيمًا. Sisters marking change or persistence keep their own aspect: صار/أصبح/أمسى/بات/ظلّ (change), ما زال/ما برح/ما فتئ/ما انفكّ (persistence) — record the auxiliary inside the span; it is data (transformation vs. constancy).",
  "4. **kana_lahu** — possession-of-attribute predication: كان له عقلٌ ورأيٌ، له لسانٌ ذَرِبٌ، ليس له صبرٌ.",
  "5. **body_part** — portrait predication on a possessed body part: رأسُه كبيرٌ، وجهُها كالقمر، عيناه غائرتان. (The suffix is a chain mention of the target — `mention_ref` it.)",
  "6. **tashbih** — comparison targeting a figure: كأنّه أسدٌ، هو كالثعلب في حِيَله. Includes the **exemplum-application formula**: إنما مَثَلُك مَثَلُ فلانٍ الذي… → the span is the *application clause only*; the embedded tale body is its own narrative level with its own figures and is **not** part of this record.",
  "7. **elative** — أفعل التفضيل: أشجعُ القوم، أشدُّهم بأسًا (keep the تمييز inside the span).",
  "8. **tamyiz** — specification of respect outside the elative: طاب نفسًا، أكثرُ منه مالًا.",
  "9. **hal** — circumstantial state: خرج غاضبًا، جاء مسرعًا. Target = صاحب الحال.",
  "10. **maf3ul_mutlaq** — manner cognate accusative: ضربه ضربًا شديدًا.",
  "11. **maf3ul_liajlih** — motive accusative: فعل ذلك حسدًا/خوفًا/طمعًا — explicit interiority through syntax; target = the agent.",
  "12. **manner_pp** — prepositional/adverbial manner on an action: في رفقٍ، بعنفٍ، سريعًا.",
  "13. **madh_dhamm** — أفعال المدح والذم: نِعم الصاحبُ، بئس الأخُ، حبّذا/لا حبّذا.",
  "14. **ta3ajjub** — صيغتا التعجب: ما أحكمَه!، أكرِمْ به!",
  "15. **tasghir** — evaluative diminutive of a figure's designation: رُجَيْل، and the like (belittlement/endearment encoded morphologically).",
  "16. **dua** — evaluative invocation for/against: رحمه الله، لعنه الله، قاتله الله ما أدهاه (record the fused دعاء + تعجب as one span, devices [dua, ta3ajjub]).",
  "17. **idafa_wasfiyya** — descriptive annexation: ذو رأيٍ، صاحبُ حيلةٍ، أهلُ الغدر؛ descriptive kunyas (أبو/أمّ + quality).",
  "18. **negated_trait** — negation as ascription: لا عقلَ له، ليس فيه خيرٌ، ما كان ليغدر.",
  "19. **vocative_eval** — evaluative المنادى: يا جاهلُ، يا خائنُ، أيها الملكُ الكريمُ (reuse the coreference vocative mention via `mention_ref`).",
  "20. **urf_reputation** — reputation verbs: عُرف بـ، اشتُهر بـ، وُصف بـ، كان يُعرف بالحكمة → `source` = `communal`.",
  "21. **naming** — (a) characterizing nominal chain forms: every definite description/epithet in a figure's chain is a standing characterization token — import by `mention_ref`, do not re-copy; (b) the **naming-motivation formula**: سُمّي بذلك لأنّ…، إنما قيل له X لـ… → extract the motivation clause.",
  "",
  "### B2. What speech does (resolving \"characterization in character speech\")",
  "Speech is a **source value, not a device**. Any B1/B3 device occurring inside a quote is recorded once, with `source = {voice:\"figure\", figure:<speaker>, channel:\"speech\"}` and `targets` = whoever it is about (possibly the speaker: self-ascription). Do **not** additionally create a record characterizing the *speaker* for having said it — that the fox flatters is an inference; downstream derives it from the source field. The same rule, with `channel:\"interior\"`, covers self-perception in thought.",
  "",
  "### B3. Action devices (indirect; the selectivity rule)",
  "A bare, unmodified action verb is narration, not characterization. Action material enters **only** through:",
  "22. **habitual_kana** — كان/ما زال + imperfect: كان يكذب، كان لا يؤذي أحدًا — habituality is the strongest action-as-trait marker in classical prose; the span = auxiliary + verb (+ object if needed for sense).",
  "23. *(marked action)* — an action span carrying devices 9–12 — already captured by those devices; no separate tag.",
  "24. **reaction** — another figure's perceptual/affective/behavioral response tied to the target as stimulus: فلمّا رأته الطيرُ فزِعت منه وطارت. Record `targets` = the stimulus figure, `experiencer` = the reacting figure(s), span = the reaction clause. (The matching رأى/فزع also yields Layer-A records — cross-link by id.)",
  "",
  "[Toggle `full_action_harvest`, default OFF: additionally list every predicate whose agent is each figure, unmodified ones included. Expect volume; use only if downstream truly consumes raw act inventories.]",
  "",
  "### B-exclusions",
  "- **Gnomic ascriptions** characterize types, not figures (إنّ العاقلَ من…) — exclude, *unless* the text explicitly applies the generalization to a figure (tashbih/exemplum application, evaluative vocative, or a deictic: وأنت ذلك الرجلُ) — then the application clause is the span.",
  "- **Species-generic statements** about an animal kind are gnomic unless predicated of the individual figure.",
  "- Identifying (restrictive) modifiers that merely pick a referent are still extracted under their device — the restrictive/descriptive judgment is interpretive and is left downstream.",
  "- Predicative NPs already excluded from chains (أنا ابنُ الملك) **are** in scope here when they ascribe role/quality — this layer is exactly where predication belongs.",
  "",
  "---",
  "",
  "## Direct/indirect as a derived mapping (not annotated)",
  "direct = devices 1–21; indirect = 22 and 24 (and any 9–12 span read as evidence rather than ascription). The extractor never writes a `mode` field; the mapping above defines it for downstream queries.",
  "",
  "## Output",
  "Return **JSON only** — no fences, no preamble.",
  "",
  "```json",
  "{",
  "  \"focalization\": [",
  "    { \"id\": \"A1\", \"class\": \"perception\", \"marker\": \"<verbatim>\", \"experiencer\": \"F2\",",
  "      \"scope\": \"<verbatim complement>\", \"context\": \"<verbatim clause>\",",
  "      \"source\": { \"voice\": \"narrator\", \"level\": 1, \"narrator\": \"F1\" } }",
  "  ],",
  "  \"characterizations\": [",
  "    { \"id\": \"C1\", \"targets\": [\"F3\"], \"devices\": [\"khabar\"], \"span\": \"<verbatim>\",",
  "      \"context\": \"<verbatim clause>\",",
  "      \"source\": { \"voice\": \"figure\", \"figure\": \"F2\", \"channel\": \"speech\" },",
  "      \"mention_ref\": \"F3.m4\" }",
  "  ],",
  "  \"unmatched\": [ { \"span\": \"<verbatim>\", \"context\": \"<verbatim>\", \"note\": \"no FIGURES id\" } ]",
  "}",
  "```",
  "- Both arrays in textual order; ids stable for downstream edges.",
  "- `experiencer`, `targets` accept group-figure ids; plural experiencers as arrays.",
  "- Defaults: inferred_trait OFF · full_action_harvest OFF · trait paraphrase never · reliability never · levels and `communal` source ON · `mention_ref` whenever the span coincides with a chain mention.",
  "",
  "## Worked micro-examples (generic — not from any particular witness)",
  "1. **Narrator, direct:** وكان للملك وزيرٌ حكيمٌ ذو رأيٍ → C: targets=[vizier], devices=[naat, idafa_wasfiyya], source narrator N1; the كان للملك frame also yields kana_lahu for the king? No — the possessed is a figure, not a trait: no record for the king. (Possession of *persons* is relationship data, downstream.)",
  "2. **Speech, other-directed:** فقال الثعلبُ للأسد إنّك لقويٌّ ولكنّك لا تنظر في عاقبة أمرك → C: targets=[lion], devices=[khabar], span إنّك لقويّ; second record devices=[negated_trait]; both source = fox/speech. No record characterizing the fox.",
  "3. **Interior + self:** قال في نفسه إني ضعيفٌ عن حربه → A5 (experiencer=he, scope=إني ضعيف عن حربه) + C: targets=[himself], devices=[khabar], source = figure/interior.",
  "4. **Reaction + focalization:** فلمّا رأته الطيرُ فزعت منه وطارت → A1 (experiencer=birds, scope=ـه) and A3 (فزعت); C: targets=[him], devices=[reaction], experiencer=[birds].",
  "5. **Habit + motive:** وكان يحمل إليه الطعامَ كلَّ يومٍ رجاءَ صلاحه → C: devices=[habitual_kana, maf3ul_liajlih], targets=[the carrier].",
  "6. **Exemplum application:** إنما مثلُك مثلُ التاجر الذي باع ما لا يملك → C: targets=[addressee], devices=[tashbih], span = the application clause only; the merchant's tale, if told, is a deeper level with its own records.",
  "7. **Evidential:** زعموا أنّه كان بأرض كذا أسدٌ → A6, experiencer = narrator level uttering زعموا. (The أنّه is ضمير الشأن — no figure attribution.)",
  "8. **Asymmetry:** ودخل عليه وهو لا يشعر → A7, experiencer = the unaware figure.",
  "",
  "---",
  "",
  "TEXT"
].join("\n");

export type FocalizationCharacterizationPromptTextBuilder = (
  sourceText: string,
  figures: readonly SingleWitnessCoreferenceChainV2Figure[],
  focusFigureId?: string | null
) => string;

export interface PrepareSingleWitnessFocalizationCharacterizationExtractionOptions {
  readonly promptVersion?: string;
  readonly promptTextBuilder?: FocalizationCharacterizationPromptTextBuilder;
  readonly focusFigureId?: string | null;
}

export interface PreparedSingleWitnessFocalizationCharacterizationExtractionCall {
  readonly promptText: string;
  readonly sourceText: string;
  readonly sourceMap: readonly CharacterExtractionSourceMapSpan[];
  readonly scope: PreparedCharacterExtractionScopeMetadata;
  readonly promptVersion: string;
  readonly figures: readonly SingleWitnessCoreferenceChainV2Figure[];
  readonly focusFigureId?: string | null;
}

export type KnownFocalizationMarkerClass =
  | "perception"
  | "cognition"
  | "affect"
  | "volition"
  | "interior_discourse"
  | "narratorial_evidential_distance"
  | "knowledge_asymmetry";

export type FocalizationMarkerClass = KnownFocalizationMarkerClass | (string & {});

export type KnownCharacterizationDevice =
  | "naat"
  | "khabar"
  | "kana_pred"
  | "kana_lahu"
  | "body_part"
  | "tashbih"
  | "elative"
  | "tamyiz"
  | "hal"
  | "maf3ul_mutlaq"
  | "maf3ul_liajlih"
  | "manner_pp"
  | "madh_dhamm"
  | "ta3ajjub"
  | "tasghir"
  | "dua"
  | "idafa_wasfiyya"
  | "negated_trait"
  | "vocative_eval"
  | "urf_reputation"
  | "naming"
  | "habitual_kana"
  | "reaction";

export type CharacterizationDevice = KnownCharacterizationDevice | (string & {});

export type FocalizationCharacterizationSource =
  | FocalizationCharacterizationNarratorSource
  | FocalizationCharacterizationFigureSource
  | FocalizationCharacterizationCommunalSource
  | (Record<string, unknown> & { readonly voice?: string });

export interface FocalizationCharacterizationNarratorSource {
  readonly voice: "narrator";
  readonly level: number;
  readonly narrator: string;
}

export interface FocalizationCharacterizationFigureSource {
  readonly voice: "figure";
  readonly figure: string;
  readonly channel: "speech" | "interior";
}

export interface FocalizationCharacterizationCommunalSource {
  readonly voice: "communal";
}

export interface SingleWitnessFocalizationCharacterizationOutput {
  readonly focalization: readonly SingleWitnessFocalizationRecord[];
  readonly characterizations: readonly SingleWitnessCharacterizationRecord[];
  readonly unmatched: readonly SingleWitnessFocalizationUnmatchedRecord[];
  readonly [key: string]: unknown;
}

export interface SingleWitnessFocalizationRecord {
  readonly id: string;
  readonly class: FocalizationMarkerClass;
  readonly marker: string;
  readonly experiencer: string | readonly string[];
  readonly scope: string;
  readonly context: string;
  readonly source: FocalizationCharacterizationSource;
  readonly [key: string]: unknown;
}

export interface SingleWitnessCharacterizationRecord {
  readonly id: string;
  readonly targets: readonly string[];
  readonly devices: readonly CharacterizationDevice[];
  readonly span: string;
  readonly context: string;
  readonly source: FocalizationCharacterizationSource;
  readonly mention_ref?: string;
  readonly [key: string]: unknown;
}

export interface SingleWitnessFocalizationUnmatchedRecord {
  readonly span: string;
  readonly context: string;
  readonly note?: string;
  readonly [key: string]: unknown;
}

export interface ValidateSingleWitnessFocalizationCharacterizationOutputOptions {
  readonly focusFigureId?: string | null;
}

export type FocalizationCharacterizationValidationIssueCode =
  | "invalid-output"
  | "invalid-focalization"
  | "invalid-focalization-record"
  | "invalid-characterizations"
  | "invalid-characterization-record"
  | "invalid-unmatched"
  | "invalid-unmatched-record"
  | "empty-id"
  | "invalid-class"
  | "empty-marker"
  | "empty-scope"
  | "empty-context"
  | "invalid-experiencer"
  | "invalid-targets"
  | "invalid-devices"
  | "invalid-device"
  | "empty-span"
  | "invalid-source"
  | "invalid-mention-ref"
  | "non-focus-focalization-experiencer"
  | "non-focus-characterization-target";

export interface FocalizationCharacterizationValidationIssue {
  readonly code: FocalizationCharacterizationValidationIssueCode;
  readonly message: string;
  readonly path: readonly (string | number)[];
  readonly recordIndex?: number;
  readonly recordKind?: "focalization" | "characterization" | "unmatched";
}

export type FocalizationCharacterizationGroundingStatus = "grounded" | "ambiguous" | "not_found";

export type FocalizationCharacterizationGroundingDiagnosticCode =
  | "empty-context"
  | "context-not-found"
  | "ambiguous-context"
  | "empty-marker"
  | "marker-not-found-in-context"
  | "ambiguous-marker"
  | "empty-scope"
  | "scope-not-found-in-context"
  | "ambiguous-scope"
  | "empty-span"
  | "span-not-found-in-context"
  | "ambiguous-span"
  | "unknown-figure-ref"
  | "unknown-mention-ref";

export interface FocalizationCharacterizationGroundingDiagnostic {
  readonly code: FocalizationCharacterizationGroundingDiagnosticCode;
  readonly message: string;
  readonly path: readonly (string | number)[];
  readonly recordIndex?: number;
  readonly recordKind?: "focalization" | "characterization" | "unmatched";
  readonly ref?: string;
}

export interface GroundedSingleWitnessFocalizationRecord extends SingleWitnessFocalizationRecord {
  readonly status: FocalizationCharacterizationGroundingStatus;
  readonly contextRange?: CharacterMentionTextRange;
  readonly markerRange?: CharacterMentionTextRange;
  readonly scopeRange?: CharacterMentionTextRange;
  readonly contextSourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly markerSourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly scopeSourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly diagnostics: readonly FocalizationCharacterizationGroundingDiagnostic[];
}

export interface GroundedSingleWitnessCharacterizationRecord extends SingleWitnessCharacterizationRecord {
  readonly status: FocalizationCharacterizationGroundingStatus;
  readonly contextRange?: CharacterMentionTextRange;
  readonly spanRange?: CharacterMentionTextRange;
  readonly contextSourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly spanSourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly diagnostics: readonly FocalizationCharacterizationGroundingDiagnostic[];
}

export interface GroundedSingleWitnessFocalizationUnmatchedRecord
  extends SingleWitnessFocalizationUnmatchedRecord {
  readonly status: FocalizationCharacterizationGroundingStatus;
  readonly contextRange?: CharacterMentionTextRange;
  readonly spanRange?: CharacterMentionTextRange;
  readonly contextSourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly spanSourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly diagnostics: readonly FocalizationCharacterizationGroundingDiagnostic[];
}

export interface GroundedSingleWitnessFocalizationCharacterizationOutput {
  readonly focalization: readonly GroundedSingleWitnessFocalizationRecord[];
  readonly characterizations: readonly GroundedSingleWitnessCharacterizationRecord[];
  readonly unmatched: readonly GroundedSingleWitnessFocalizationUnmatchedRecord[];
  readonly diagnostics: readonly FocalizationCharacterizationGroundingDiagnostic[];
}

export interface GroundSingleWitnessFocalizationCharacterizationOptions {
  readonly maxCandidateRanges?: number;
}

interface PromptFigure {
  readonly id: string;
  readonly kind: string;
  readonly anchor: {
    readonly name: string;
    readonly transliteration: string;
  };
  readonly chain: readonly PromptFigureMention[];
}

interface PromptFigureMention {
  readonly id: string;
  readonly mention: string;
  readonly context: string;
  readonly with?: readonly string[];
}

const toPromptFigures = (
  figures: readonly SingleWitnessCoreferenceChainV2Figure[]
): PromptFigure[] => (
  figures.map((figure) => ({
    id: figure.id,
    kind: figure.kind,
    anchor: {
      name: figure.anchor.name,
      transliteration: figure.anchor.transliteration
    },
    chain: figure.chain.map((mention) => ({
      id: mention.id,
      mention: mention.mention,
      context: mention.context,
      ...(mention.with !== undefined ? { with: mention.with } : {})
    }))
  }))
);

const normalizeFocusFigureId = (focusFigureId: string | null | undefined): string | null => {
  if (typeof focusFigureId !== "string") {
    return null;
  }
  const trimmed = focusFigureId.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const buildFocusFigureBlock = (focusFigureId: string | null): string => {
  if (!focusFigureId) {
    return "";
  }

  return [
    "",
    "FOCUS_FIGURE",
    focusFigureId,
    "",
    "Focused mode: keep the same output shape `{ focalization: [], characterizations: [], unmatched: [] }`. Return only focalization records where `experiencer` is the FOCUS_FIGURE id, and characterization records where `targets` includes the FOCUS_FIGURE id. Other figure ids may appear only where the schema naturally needs them, such as `source.figure`, reaction `experiencer`, or context attribution. Do not identify new figures or rename the focus figure."
  ].join("\n");
};

export const buildSingleWitnessFocalizationCharacterizationPromptText = (
  sourceText: string,
  figures: readonly SingleWitnessCoreferenceChainV2Figure[],
  focusFigureId?: string | null
): string => (
  `${FOCALIZATION_CHARACTERIZATION_PROMPT_PREFIX}\n${sourceText}\n\nFIGURES\n${JSON.stringify(toPromptFigures(figures), null, 2)}${buildFocusFigureBlock(normalizeFocusFigureId(focusFigureId))}`
);

export const prepareSingleWitnessFocalizationCharacterizationExtractionCall = (
  input: CharacterIndexInput,
  scope: CharacterExtractionScope,
  figures: readonly SingleWitnessCoreferenceChainV2Figure[],
  options: PrepareSingleWitnessFocalizationCharacterizationExtractionOptions = {}
): PreparedSingleWitnessFocalizationCharacterizationExtractionCall => {
  const sourceCall = prepareSingleWitnessCharacterExtractionCall(input, scope);
  const promptVersion =
    options.promptVersion || SINGLE_WITNESS_FOCALIZATION_CHARACTERIZATION_EXTRACTION_PROMPT_VERSION;
  const promptTextBuilder =
    options.promptTextBuilder || buildSingleWitnessFocalizationCharacterizationPromptText;
  const focusFigureId = normalizeFocusFigureId(options.focusFigureId);

  return {
    promptText: promptTextBuilder(sourceCall.sourceText, figures, focusFigureId),
    sourceText: sourceCall.sourceText,
    sourceMap: sourceCall.sourceMap,
    scope: sourceCall.scope,
    promptVersion,
    figures,
    ...(focusFigureId ? { focusFigureId } : {})
  };
};

export const validatePreparedFocalizationCharacterizationExtractionCall = (
  call: PreparedSingleWitnessFocalizationCharacterizationExtractionCall
): PreparedCharacterExtractionValidationIssue[] => validatePreparedExtractionCall(call);

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === "object" && value !== null && !Array.isArray(value)
);

const isNonEmptyString = (value: unknown): value is string => (
  typeof value === "string" && value.trim().length > 0
);

const pushValidationIssue = (
  issues: FocalizationCharacterizationValidationIssue[],
  issue: FocalizationCharacterizationValidationIssue
): void => {
  issues.push(issue);
};

const validateSource = (
  value: unknown,
  issues: FocalizationCharacterizationValidationIssue[],
  path: readonly (string | number)[],
  recordKind: "focalization" | "characterization",
  recordIndex: number
): void => {
  if (!isRecord(value)) {
    pushValidationIssue(issues, {
      code: "invalid-source",
      message: "Focalization/characterization source must be an object.",
      path,
      recordKind,
      recordIndex
    });
    return;
  }
};

export const validateSingleWitnessFocalizationCharacterizationOutput = (
  output: unknown,
  options: ValidateSingleWitnessFocalizationCharacterizationOutputOptions = {}
): FocalizationCharacterizationValidationIssue[] => {
  const issues: FocalizationCharacterizationValidationIssue[] = [];
  const focusFigureId = normalizeFocusFigureId(options.focusFigureId);

  if (!isRecord(output)) {
    pushValidationIssue(issues, {
      code: "invalid-output",
      message: "Focalization/characterization output must be an object.",
      path: []
    });
    return issues;
  }

  if (!Array.isArray(output.focalization)) {
    pushValidationIssue(issues, {
      code: "invalid-focalization",
      message: "Output must include a focalization array.",
      path: ["focalization"]
    });
  } else {
    output.focalization.forEach((record, recordIndex) => {
      const path = ["focalization", recordIndex] as const;
      if (!isRecord(record)) {
        pushValidationIssue(issues, {
          code: "invalid-focalization-record",
          message: "Focalization record must be an object.",
          path,
          recordKind: "focalization",
          recordIndex
        });
        return;
      }

      if (!isNonEmptyString(record.id)) {
        pushValidationIssue(issues, {
          code: "empty-id",
          message: "Focalization record must include a non-empty id.",
          path: [...path, "id"],
          recordKind: "focalization",
          recordIndex
        });
      }

      if (!isNonEmptyString(record.class)) {
        pushValidationIssue(issues, {
          code: "invalid-class",
          message: "Focalization class must be a non-empty string.",
          path: [...path, "class"],
          recordKind: "focalization",
          recordIndex
        });
      }

      if (!isNonEmptyString(record.marker)) {
        pushValidationIssue(issues, {
          code: "empty-marker",
          message: "Focalization record must include a non-empty marker.",
          path: [...path, "marker"],
          recordKind: "focalization",
          recordIndex
        });
      }

      if (!isNonEmptyString(record.scope)) {
        pushValidationIssue(issues, {
          code: "empty-scope",
          message: "Focalization record must include a non-empty scope.",
          path: [...path, "scope"],
          recordKind: "focalization",
          recordIndex
        });
      }

      if (!isNonEmptyString(record.context)) {
        pushValidationIssue(issues, {
          code: "empty-context",
          message: "Focalization record must include a non-empty context.",
          path: [...path, "context"],
          recordKind: "focalization",
          recordIndex
        });
      }

      if (
        !isNonEmptyString(record.experiencer) &&
        !(
          Array.isArray(record.experiencer) &&
          record.experiencer.length > 0 &&
          record.experiencer.every(isNonEmptyString)
        )
      ) {
        pushValidationIssue(issues, {
          code: "invalid-experiencer",
          message: "Focalization experiencer must be a non-empty string or string array.",
          path: [...path, "experiencer"],
          recordKind: "focalization",
          recordIndex
        });
      }

      if (focusFigureId) {
        const matchesFocus = typeof record.experiencer === "string"
          ? record.experiencer === focusFigureId
          : Array.isArray(record.experiencer) &&
            record.experiencer.length === 1 &&
            record.experiencer[0] === focusFigureId;
        if (!matchesFocus) {
          pushValidationIssue(issues, {
            code: "non-focus-focalization-experiencer",
            message: "Focused focalization output must use the focus figure id as experiencer.",
            path: [...path, "experiencer"],
            recordKind: "focalization",
            recordIndex
          });
        }
      }

      validateSource(record.source, issues, [...path, "source"], "focalization", recordIndex);
    });
  }

  if (!Array.isArray(output.characterizations)) {
    pushValidationIssue(issues, {
      code: "invalid-characterizations",
      message: "Output must include a characterizations array.",
      path: ["characterizations"]
    });
  } else {
    output.characterizations.forEach((record, recordIndex) => {
      const path = ["characterizations", recordIndex] as const;
      if (!isRecord(record)) {
        pushValidationIssue(issues, {
          code: "invalid-characterization-record",
          message: "Characterization record must be an object.",
          path,
          recordKind: "characterization",
          recordIndex
        });
        return;
      }

      if (!isNonEmptyString(record.id)) {
        pushValidationIssue(issues, {
          code: "empty-id",
          message: "Characterization record must include a non-empty id.",
          path: [...path, "id"],
          recordKind: "characterization",
          recordIndex
        });
      }

      if (!Array.isArray(record.targets) || !record.targets.every(isNonEmptyString)) {
        pushValidationIssue(issues, {
          code: "invalid-targets",
          message: "Characterization targets must be an array of non-empty figure ids.",
          path: [...path, "targets"],
          recordKind: "characterization",
          recordIndex
        });
      } else if (focusFigureId && !record.targets.includes(focusFigureId)) {
        pushValidationIssue(issues, {
          code: "non-focus-characterization-target",
          message: "Focused characterization output must include the focus figure id in targets.",
          path: [...path, "targets"],
          recordKind: "characterization",
          recordIndex
        });
      }

      if (!Array.isArray(record.devices) || record.devices.length === 0) {
        pushValidationIssue(issues, {
          code: "invalid-devices",
          message: "Characterization devices must be a non-empty array.",
          path: [...path, "devices"],
          recordKind: "characterization",
          recordIndex
        });
      } else {
        record.devices.forEach((device, deviceIndex) => {
          if (!isNonEmptyString(device)) {
            pushValidationIssue(issues, {
              code: "invalid-device",
              message: "Characterization device must be a non-empty string.",
              path: [...path, "devices", deviceIndex],
              recordKind: "characterization",
              recordIndex
            });
          }
        });
      }

      if (!isNonEmptyString(record.span)) {
        pushValidationIssue(issues, {
          code: "empty-span",
          message: "Characterization record must include a non-empty span.",
          path: [...path, "span"],
          recordKind: "characterization",
          recordIndex
        });
      }

      if (!isNonEmptyString(record.context)) {
        pushValidationIssue(issues, {
          code: "empty-context",
          message: "Characterization record must include a non-empty context.",
          path: [...path, "context"],
          recordKind: "characterization",
          recordIndex
        });
      }

      if (record.mention_ref !== undefined && !isNonEmptyString(record.mention_ref)) {
        pushValidationIssue(issues, {
          code: "invalid-mention-ref",
          message: "mention_ref must be a non-empty string when present.",
          path: [...path, "mention_ref"],
          recordKind: "characterization",
          recordIndex
        });
      }

      validateSource(record.source, issues, [...path, "source"], "characterization", recordIndex);
    });
  }

  if (!Array.isArray(output.unmatched)) {
    pushValidationIssue(issues, {
      code: "invalid-unmatched",
      message: "Output must include an unmatched array.",
      path: ["unmatched"]
    });
  } else {
    output.unmatched.forEach((record, recordIndex) => {
      const path = ["unmatched", recordIndex] as const;
      if (!isRecord(record)) {
        pushValidationIssue(issues, {
          code: "invalid-unmatched-record",
          message: "Unmatched record must be an object.",
          path,
          recordKind: "unmatched",
          recordIndex
        });
        return;
      }
      if (!isNonEmptyString(record.span)) {
        pushValidationIssue(issues, {
          code: "empty-span",
          message: "Unmatched record must include a non-empty span.",
          path: [...path, "span"],
          recordKind: "unmatched",
          recordIndex
        });
      }
      if (!isNonEmptyString(record.context)) {
        pushValidationIssue(issues, {
          code: "empty-context",
          message: "Unmatched record must include a non-empty context.",
          path: [...path, "context"],
          recordKind: "unmatched",
          recordIndex
        });
      }
    });
  }

  return issues;
};

const findExactRanges = (
  sourceText: string,
  text: string
): CharacterMentionTextRange[] => {
  if (!text) {
    return [];
  }

  const ranges: CharacterMentionTextRange[] = [];
  let cursor = 0;
  while (cursor <= sourceText.length) {
    const start = sourceText.indexOf(text, cursor);
    if (start < 0) {
      break;
    }
    const end = start + text.length;
    ranges.push({ start, end });
    cursor = start + 1;
  }
  return ranges;
};

const limitCandidateRanges = (
  ranges: readonly CharacterMentionTextRange[],
  options: GroundSingleWitnessFocalizationCharacterizationOptions
): readonly CharacterMentionTextRange[] => {
  if (typeof options.maxCandidateRanges !== "number") {
    return ranges;
  }
  return ranges.slice(0, Math.max(0, Math.floor(options.maxCandidateRanges)));
};

const toAbsoluteRanges = (
  contextRange: CharacterMentionTextRange,
  relativeRanges: readonly CharacterMentionTextRange[]
): CharacterMentionTextRange[] => (
  relativeRanges.map((range) => ({
    start: contextRange.start + range.start,
    end: contextRange.start + range.end
  }))
);

const groundingDiagnostic = (
  code: FocalizationCharacterizationGroundingDiagnosticCode,
  message: string,
  path: readonly (string | number)[],
  recordKind: "focalization" | "characterization" | "unmatched",
  recordIndex: number,
  ref?: string
): FocalizationCharacterizationGroundingDiagnostic => ({
  code,
  message,
  path,
  recordKind,
  recordIndex,
  ...(ref !== undefined ? { ref } : {})
});

const mapRangeSafely = (
  call: Pick<PreparedSingleWitnessFocalizationCharacterizationExtractionCall, "sourceText" | "sourceMap">,
  range: CharacterMentionTextRange | undefined
): readonly CharacterIndexSourceAnchor[] => {
  if (!range) {
    return [];
  }
  return mapValidatedWindowRangeToSourceAnchors(call, range);
};

interface GroundedField {
  readonly range?: CharacterMentionTextRange;
  readonly candidateRanges: readonly CharacterMentionTextRange[];
  readonly sourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly diagnostics: readonly FocalizationCharacterizationGroundingDiagnostic[];
}

const groundTextWithinContext = (
  call: Pick<PreparedSingleWitnessFocalizationCharacterizationExtractionCall, "sourceText" | "sourceMap">,
  contextRange: CharacterMentionTextRange | undefined,
  contextText: string,
  text: string,
  field: "marker" | "scope" | "span",
  path: readonly (string | number)[],
  recordKind: "focalization" | "characterization" | "unmatched",
  recordIndex: number,
  options: GroundSingleWitnessFocalizationCharacterizationOptions
): GroundedField => {
  if (text.trim().length === 0) {
    return {
      candidateRanges: [],
      sourceAnchors: [],
      diagnostics: [
        groundingDiagnostic(
          field === "marker" ? "empty-marker" : field === "scope" ? "empty-scope" : "empty-span",
          `${field} is empty.`,
          path,
          recordKind,
          recordIndex
        )
      ]
    };
  }

  if (!contextRange) {
    return {
      candidateRanges: [],
      sourceAnchors: [],
      diagnostics: []
    };
  }

  const relativeRanges = findExactRanges(contextText, text);
  const ranges = toAbsoluteRanges(contextRange, relativeRanges);
  const candidateRanges = limitCandidateRanges(ranges, options);
  if (ranges.length === 0) {
    return {
      candidateRanges,
      sourceAnchors: [],
      diagnostics: [
        groundingDiagnostic(
          field === "marker"
            ? "marker-not-found-in-context"
            : field === "scope"
              ? "scope-not-found-in-context"
              : "span-not-found-in-context",
          `${field} does not occur inside its grounded context.`,
          path,
          recordKind,
          recordIndex
        )
      ]
    };
  }

  if (ranges.length > 1) {
    return {
      candidateRanges,
      sourceAnchors: [],
      diagnostics: [
        groundingDiagnostic(
          field === "marker"
            ? "ambiguous-marker"
            : field === "scope"
              ? "ambiguous-scope"
              : "ambiguous-span",
          `${field} occurs more than once inside its grounded context.`,
          path,
          recordKind,
          recordIndex
        )
      ]
    };
  }

  const range = ranges[0]!;
  return {
    range,
    candidateRanges,
    sourceAnchors: mapRangeSafely(call, range),
    diagnostics: []
  };
};

const groundContext = (
  call: Pick<PreparedSingleWitnessFocalizationCharacterizationExtractionCall, "sourceText" | "sourceMap">,
  context: string,
  path: readonly (string | number)[],
  recordKind: "focalization" | "characterization" | "unmatched",
  recordIndex: number,
  options: GroundSingleWitnessFocalizationCharacterizationOptions
): GroundedField & { readonly contextText: string } => {
  if (context.trim().length === 0) {
    return {
      contextText: "",
      candidateRanges: [],
      sourceAnchors: [],
      diagnostics: [
        groundingDiagnostic(
          "empty-context",
          "context is empty.",
          path,
          recordKind,
          recordIndex
        )
      ]
    };
  }

  const ranges = findExactRanges(call.sourceText, context);
  const candidateRanges = limitCandidateRanges(ranges, options);
  if (ranges.length === 0) {
    return {
      contextText: "",
      candidateRanges,
      sourceAnchors: [],
      diagnostics: [
        groundingDiagnostic(
          "context-not-found",
          "context does not occur in the prepared sourceText.",
          path,
          recordKind,
          recordIndex
        )
      ]
    };
  }

  if (ranges.length > 1) {
    return {
      contextText: "",
      candidateRanges,
      sourceAnchors: [],
      diagnostics: [
        groundingDiagnostic(
          "ambiguous-context",
          "context occurs more than once in the prepared sourceText.",
          path,
          recordKind,
          recordIndex
        )
      ]
    };
  }

  const range = ranges[0]!;
  return {
    contextText: call.sourceText.slice(range.start, range.end),
    range,
    candidateRanges,
    sourceAnchors: mapRangeSafely(call, range),
    diagnostics: []
  };
};

const statusFromDiagnostics = (
  diagnostics: readonly FocalizationCharacterizationGroundingDiagnostic[]
): FocalizationCharacterizationGroundingStatus => {
  if (
    diagnostics.some((diagnostic) => (
      diagnostic.code.includes("not-found") ||
      diagnostic.code.startsWith("empty") ||
      diagnostic.code.startsWith("unknown")
    ))
  ) {
    return "not_found";
  }
  if (diagnostics.some((diagnostic) => diagnostic.code.startsWith("ambiguous"))) {
    return "ambiguous";
  }
  return "grounded";
};

const collectFigureIds = (
  figures: readonly SingleWitnessCoreferenceChainV2Figure[]
): Set<string> => new Set(figures.map((figure) => figure.id));

const collectMentionIds = (
  figures: readonly SingleWitnessCoreferenceChainV2Figure[]
): Set<string> => (
  new Set(figures.flatMap((figure) => figure.chain.map((mention: SingleWitnessCoreferenceChainV2Mention) => mention.id)))
);

const isNarratorLevelRef = (ref: string): boolean => /^N\d+$/.test(ref);

const sourceFigureRefs = (source: FocalizationCharacterizationSource): string[] => {
  if (source.voice === "figure" && typeof source.figure === "string") {
    return [source.figure];
  }
  if (source.voice === "narrator" && typeof source.narrator === "string" && source.narrator !== "frame") {
    return [source.narrator];
  }
  return [];
};

const refDiagnostics = (
  refs: readonly string[],
  figureIds: ReadonlySet<string>,
  path: readonly (string | number)[],
  recordKind: "focalization" | "characterization",
  recordIndex: number
): FocalizationCharacterizationGroundingDiagnostic[] => (
  refs
    .filter((ref) => !figureIds.has(ref) && ref !== "frame" && !isNarratorLevelRef(ref))
    .map((ref) => groundingDiagnostic(
      "unknown-figure-ref",
      "Figure reference does not occur in FIGURES.",
      path,
      recordKind,
      recordIndex,
      ref
    ))
);

export const groundSingleWitnessFocalizationCharacterization = (
  call: Pick<
    PreparedSingleWitnessFocalizationCharacterizationExtractionCall,
    "sourceText" | "sourceMap" | "figures"
  >,
  output: SingleWitnessFocalizationCharacterizationOutput,
  options: GroundSingleWitnessFocalizationCharacterizationOptions = {}
): GroundedSingleWitnessFocalizationCharacterizationOutput => {
  const figureIds = collectFigureIds(call.figures);
  const mentionIds = collectMentionIds(call.figures);

  const focalization = output.focalization.map((record, recordIndex) => {
    const context = groundContext(
      call,
      record.context,
      ["focalization", recordIndex, "context"],
      "focalization",
      recordIndex,
      options
    );
    const marker = groundTextWithinContext(
      call,
      context.range,
      context.contextText,
      record.marker,
      "marker",
      ["focalization", recordIndex, "marker"],
      "focalization",
      recordIndex,
      options
    );
    const scope = groundTextWithinContext(
      call,
      context.range,
      context.contextText,
      record.scope,
      "scope",
      ["focalization", recordIndex, "scope"],
      "focalization",
      recordIndex,
      options
    );
    const experiencerRefs = Array.isArray(record.experiencer)
      ? record.experiencer
      : [record.experiencer];
    const diagnostics = [
      ...context.diagnostics,
      ...marker.diagnostics,
      ...scope.diagnostics,
      ...refDiagnostics(
        [...experiencerRefs, ...sourceFigureRefs(record.source)],
        figureIds,
        ["focalization", recordIndex],
        "focalization",
        recordIndex
      )
    ];

    return {
      ...record,
      status: statusFromDiagnostics(diagnostics),
      contextRange: context.range,
      markerRange: marker.range,
      scopeRange: scope.range,
      contextSourceAnchors: context.sourceAnchors,
      markerSourceAnchors: marker.sourceAnchors,
      scopeSourceAnchors: scope.sourceAnchors,
      diagnostics
    };
  });

  const characterizations = output.characterizations.map((record, recordIndex) => {
    const context = groundContext(
      call,
      record.context,
      ["characterizations", recordIndex, "context"],
      "characterization",
      recordIndex,
      options
    );
    const span = groundTextWithinContext(
      call,
      context.range,
      context.contextText,
      record.span,
      "span",
      ["characterizations", recordIndex, "span"],
      "characterization",
      recordIndex,
      options
    );
    const figureDiagnostics = refDiagnostics(
      [...record.targets, ...sourceFigureRefs(record.source)],
      figureIds,
      ["characterizations", recordIndex],
      "characterization",
      recordIndex
    );
    const mentionDiagnostics = record.mention_ref && !mentionIds.has(record.mention_ref)
      ? [
          groundingDiagnostic(
            "unknown-mention-ref",
            "mention_ref does not occur in FIGURES chains.",
            ["characterizations", recordIndex, "mention_ref"],
            "characterization",
            recordIndex,
            record.mention_ref
          )
        ]
      : [];
    const diagnostics = [
      ...context.diagnostics,
      ...span.diagnostics,
      ...figureDiagnostics,
      ...mentionDiagnostics
    ];

    return {
      ...record,
      status: statusFromDiagnostics(diagnostics),
      contextRange: context.range,
      spanRange: span.range,
      contextSourceAnchors: context.sourceAnchors,
      spanSourceAnchors: span.sourceAnchors,
      diagnostics
    };
  });

  const unmatched = output.unmatched.map((record, recordIndex) => {
    const context = groundContext(
      call,
      record.context,
      ["unmatched", recordIndex, "context"],
      "unmatched",
      recordIndex,
      options
    );
    const span = groundTextWithinContext(
      call,
      context.range,
      context.contextText,
      record.span,
      "span",
      ["unmatched", recordIndex, "span"],
      "unmatched",
      recordIndex,
      options
    );
    const diagnostics = [...context.diagnostics, ...span.diagnostics];

    return {
      ...record,
      status: statusFromDiagnostics(diagnostics),
      contextRange: context.range,
      spanRange: span.range,
      contextSourceAnchors: context.sourceAnchors,
      spanSourceAnchors: span.sourceAnchors,
      diagnostics
    };
  });

  return {
    focalization,
    characterizations,
    unmatched,
    diagnostics: [
      ...focalization.flatMap((record) => record.diagnostics),
      ...characterizations.flatMap((record) => record.diagnostics),
      ...unmatched.flatMap((record) => record.diagnostics)
    ]
  };
};
